import { cn } from '@/lib/utils';
import { PRODUCT_LOGO_ALT, PRODUCT_LOGO_ON_DARK_SRC, PRODUCT_LOGO_SRC } from '@/assets/brand/product-logo';

export type ProductLogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<ProductLogoSize, string> = {
  xs: 'size-5',
  sm: 'size-7',
  md: 'size-9',
  lg: 'size-14',
  xl: 'size-20',
};

/** Hero / loader circles — logo fills more of the ring without a white tile. */
const HERO_SIZE_CLASS: Record<ProductLogoSize, string> = {
  xs: 'size-8',
  sm: 'size-12',
  md: 'size-16',
  lg: 'size-24',
  xl: 'size-28',
};

export interface ProductLogoProps {
  size?: ProductLogoSize;
  className?: string;
  frameClassName?: string;
  /**
   * Soft bordered tile for settings previews on white cards.
   * @deprecated Prefer `elevated`.
   */
  framed?: boolean;
  /** Soft bordered tile for settings previews on white cards. */
  elevated?: boolean;
  /** Drop shadow for dark surfaces (sidebar, login hero). */
  onDark?: boolean;
  /** Larger mark inside hero / loader circles. */
  hero?: boolean;
  alt?: string;
}

/**
 * DentXE product logo mark — transparent PNG, tuned per surface.
 */
export function ProductLogo({
  size = 'md',
  className,
  frameClassName,
  framed = false,
  elevated = false,
  onDark = false,
  hero = false,
  alt = PRODUCT_LOGO_ALT,
}: ProductLogoProps): JSX.Element {
  const showElevated = elevated || framed;
  const hasExplicitSize =
    className != null && (/\bsize-/.test(className) || /\bh-/.test(className) || /\bw-/.test(className));
  const dimensionClass = hero ? HERO_SIZE_CLASS[size] : SIZE_CLASS[size];
  const logoSrc = onDark ? PRODUCT_LOGO_ON_DARK_SRC : PRODUCT_LOGO_SRC;

  const image = (
    <img
      src={logoSrc}
      alt={alt}
      width={256}
      height={256}
      decoding="async"
      className={cn(
        'object-contain',
        !hasExplicitSize && !showElevated && dimensionClass,
        onDark && 'drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]',
        showElevated && 'size-full',
        className,
      )}
    />
  );

  if (!showElevated) {
    return image;
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-surface-200 bg-surface-50 p-2.5',
        SIZE_CLASS[size],
        frameClassName,
      )}
    >
      {image}
    </div>
  );
}
